import { ArrowRight, CreditCard } from "lucide-react";
import {
  type MetaFunction,
  useLoaderData,
  useSearchParams,
} from "react-router";

import { formatPrice } from "@/site/billing/config";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/ui/accordion";
import { Badge } from "@/ui/badge";
import { Button } from "@/ui/button";

import { CourseFooter } from "./course-footer";
import { CourseShell } from "./course-shell";
import { parseCourseReturnTo } from "./navigation";
import { CoursePricingPlans } from "./pricing-plans";
import type { PricingLoaderResponse } from "./types";

export const pricingMeta: MetaFunction = () => {
  return [
    { title: `Full-access pricing | ${publicEnv.siteTitle}` },
    {
      name: "description",
      content:
        "Choose lifetime access or a 30-day pass for the Makefile FYI course.",
    },
  ];
};

export function PricingPage() {
  const { dbUser, hasLockedPricing, pricing } =
    useLoaderData<PricingLoaderResponse>();
  const [searchParams] = useSearchParams();
  const returnTo = parseCourseReturnTo(searchParams.get("returnTo"));

  return (
    <CourseShell dbUser={dbUser}>
      <main className="mx-auto flex w-[calc(100%-2rem)] max-w-5xl flex-col gap-12 pb-20 sm:w-[calc(100%-3rem)] sm:gap-16 sm:pb-28">
        <section className="w-full pt-10 sm:pt-14 lg:pt-16">
          <div className="flex max-w-3xl flex-col gap-7">
            <Badge className="w-fit font-mono shadow-xs" variant="secondary">
              <CreditCard />
              Pricing
            </Badge>

            <div className="grid gap-4">
              <h1 className="text-4xl leading-tight font-semibold text-balance sm:text-5xl">
                Full-access pricing
              </h1>
              <p className="text-muted-foreground max-w-2xl text-lg leading-8 text-pretty">
                Pay once or start with a 30-day pass. Every pass counts toward
                lifetime access, with no subscription or automatic renewal.
              </p>
            </div>

            <Button asChild className="w-full justify-center sm:w-fit">
              <a href="#plans">
                Choose a plan
                <ArrowRight />
              </a>
            </Button>
          </div>
        </section>

        <section
          aria-labelledby="pricing-protection-title"
          className="bg-muted/30 grid max-w-3xl gap-3 border-y px-5 py-6 sm:px-6"
        >
          <Badge className="w-fit font-mono shadow-xs" variant="outline">
            Price protection
          </Badge>
          <h2
            className="text-2xl leading-tight font-semibold"
            id="pricing-protection-title"
          >
            {hasLockedPricing
              ? "Your lifetime target is locked"
              : "Your first successful purchase locks this offer"}
          </h2>
          <p className="text-muted-foreground leading-7">
            {hasLockedPricing ? (
              <>
                Your first successful purchase locked your{" "}
                {formatPrice(pricing.unitPriceCents)} unit price,{" "}
                {pricing.passDurationDays}-day pass duration, and{" "}
                {pricing.lifetimeUnitCount}-unit (
                {formatPrice(pricing.lifetimePriceCents)}) lifetime target.
                Later public price changes do not affect them.
              </>
            ) : (
              <>
                The public lifetime price may increase as the course grows. Your
                first successful purchase—not account creation—locks the{" "}
                {formatPrice(pricing.unitPriceCents)} unit price,{" "}
                {pricing.passDurationDays}-day pass duration, and{" "}
                {pricing.lifetimeUnitCount}-unit (
                {formatPrice(pricing.lifetimePriceCents)}) lifetime target
                offered today.
              </>
            )}
          </p>
        </section>

        <section
          aria-labelledby="pricing-plans-title"
          className="grid scroll-mt-20 gap-6"
          id="plans"
        >
          <div className="grid max-w-2xl gap-3">
            <h2
              className="text-3xl leading-tight font-semibold"
              id="pricing-plans-title"
            >
              Two payment paths
            </h2>
            <p className="text-muted-foreground leading-7">
              Choose permanent access now or learn for 30 days and keep every
              payment as lifetime progress.
            </p>
          </div>

          <CoursePricingPlans pricing={pricing} returnTo={returnTo} />
          <p className="text-muted-foreground text-center text-xs">
            Prices are in USD. Checkout is securely handled by Stripe.
            {returnTo && " You’ll return to your requested content afterward."}
          </p>
        </section>

        <section
          aria-labelledby="pricing-value-title"
          className="border-border/70 grid gap-6 border-y py-8 sm:py-10"
        >
          <div className="grid max-w-3xl gap-3">
            <h2
              className="text-3xl leading-tight font-semibold"
              id="pricing-value-title"
            >
              What full access includes
            </h2>
            <p className="text-muted-foreground leading-7">
              Lifetime access and an active pass unlock the same lessons,
              concepts, repository snapshots, and interactive exercises.
            </p>
          </div>

          <dl className="border-border grid border-y md:grid-cols-2">
            <div className="grid content-start gap-2 py-5 md:pr-5">
              <dt className="font-medium">Focused lessons</dt>
              <dd className="text-muted-foreground leading-7">
                Follow core modules that explain each design choice, then use
                the appendix as a supporting reference without losing the
                underlying commit history.
              </dd>
            </div>
            <div className="border-border grid content-start gap-2 border-t py-5 md:border-t-0 md:border-l md:pl-5">
              <dt className="font-medium">Concept guide</dt>
              <dd className="text-muted-foreground leading-7">
                Move beyond commit order with subject-focused explanations,
                worked examples, and interactive exercises.
              </dd>
            </div>
            <div className="border-border grid content-start gap-2 border-t py-5 md:pr-5">
              <dt className="font-medium">Repository snapshots</dt>
              <dd className="text-muted-foreground leading-7">
                Browse every commit, understand its position in history, and
                open the complete file behind each focused code clip.
              </dd>
            </div>
            <div className="border-border grid content-start gap-2 border-t py-5 md:border-l md:pl-5">
              <dt className="font-medium">Guided terminal replays</dt>
              <dd className="text-muted-foreground leading-7">
                Step through the commands and outputs that demonstrate each Make
                behavior.
              </dd>
            </div>
          </dl>
        </section>

        <PricingFaq hasLockedPricing={hasLockedPricing} pricing={pricing} />
      </main>

      <CourseFooter />
    </CourseShell>
  );
}

function PricingFaq({
  hasLockedPricing,
  pricing,
}: {
  hasLockedPricing: boolean;
  pricing: PricingLoaderResponse["pricing"];
}) {
  const faqItems = [
    {
      answer: (
        <>
          Makefile FYI is a growing course built from a real repository. As new
          commits introduce useful production decisions, they can become new
          lessons. There is no fixed publication schedule, and the course page
          always shows the material available today.
        </>
      ),
      question: "Will more lessons be added?",
    },
    {
      answer: (
        <>
          The public lifetime price may increase as the course grows.{" "}
          {hasLockedPricing
            ? "Your first successful purchase already locked"
            : "Your first successful purchase will lock"}{" "}
          the {formatPrice(pricing.unitPriceCents)} unit price,{" "}
          {pricing.passDurationDays}-day pass duration, and{" "}
          {pricing.lifetimeUnitCount}-unit (
          {formatPrice(pricing.lifetimePriceCents)}) lifetime target shown here.
          Creating an account or opening Checkout does not lock an offer.
        </>
      ),
      question: "Can the price increase later?",
    },
    {
      answer: `A ${pricing.passDurationDays}-day pass is a one-time ${formatPrice(
        pricing.unitPriceCents,
      )} payment. It grants full course access for ${pricing.passDurationDays} days and never renews automatically.`,
      question: "How does a 30-day pass work?",
    },
    {
      answer: `Yes. Each pass adds one of the ${pricing.lifetimeUnitCount} units needed for lifetime access. Your target is locked by your first successful purchase, and your Billing page always shows the remaining balance.`,
      question: "Does every pass count toward lifetime access?",
    },
    {
      answer: `Lifetime access unlocks automatically after ${pricing.lifetimeUnitCount} paid units. You can reach it one pass at a time or pay all remaining units in one Checkout.`,
      question: `What happens after unit ${pricing.lifetimeUnitCount}?`,
    },
    {
      answer: `Yes. If you have paid three of ${pricing.lifetimeUnitCount} units, Billing offers the remaining ${formatPrice(
        pricing.lifetimePriceCents - 3 * pricing.unitPriceCents,
      )} as a one-time purchase.`,
      question: "Can I pay the remaining balance early?",
    },
    {
      answer:
        "Paid lessons lock again when a pass expires, while free foundations remain available. Your purchased units and locked lifetime target stay on your account, so you can continue whenever you choose.",
      question: "What happens when a pass expires?",
    },
    {
      answer:
        "Yes. Lifetime access and an active pass unlock the same paid lessons, concepts, source snapshots, and terminal replays. Lifetime access also includes future learning content.",
      question: "Do both options include the same access?",
    },
    {
      answer:
        "Yes. The first lesson in each core module and in the appendix is free, as is the foundation of each concept section. Repository metadata and public summaries remain available while you evaluate full access.",
      question: "Can I try the course before paying?",
    },
    {
      answer:
        "Yes. Signing in before checkout attaches learning access and payment progress to your account.",
      question: "Do I need an account?",
    },
  ] as const;

  return (
    <section aria-labelledby="pricing-faq-title" className="grid gap-6">
      <div className="grid max-w-2xl gap-3">
        <h2
          className="text-3xl leading-tight font-semibold"
          id="pricing-faq-title"
        >
          Pricing FAQ
        </h2>
        <p className="text-muted-foreground leading-7">
          Details about passes, lifetime progress, and permanent access.
        </p>
      </div>

      <Accordion collapsible className="border-y" type="single">
        {faqItems.map((item) => (
          <AccordionItem key={item.question} value={item.question}>
            <AccordionTrigger>{item.question}</AccordionTrigger>
            <AccordionContent>
              <p className="text-muted-foreground max-w-3xl leading-6">
                {item.answer}
              </p>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </section>
  );
}
